import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { ReservedPaymentOptionIds } from '../../../../types/types';
import { FormStatus } from '../../../../types/form-state';
import { useFormActions } from '../../Hooks/useFormActions';
import PriceOptionDropdown, {
  PriceChoice,
} from '../PriceOptionDropdownContainer/PriceOptionDropdown/PriceOptionDropdown';

export interface ParticipantVariantPickerProps {
  serviceData: ServiceData;
  participantIndex: number;
  status: FormStatus;
}

/**
 * Single-select dynamic-price picker for one participant (collect-participant-form
 * flow, CUSTOM pricing). Each participant picks their own variant; the choice is
 * written to that participant's slot and the aggregate is re-synced for pricing.
 * Assumes a single variant type per service (one CustomOption).
 */
const ParticipantVariantPicker: React.FC<ParticipantVariantPickerProps> = ({
  serviceData,
  participantIndex,
  status,
}) => {
  const { t } = useTranslation();
  const { onParticipantVariantSelect } = useFormActions();

  const slotService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const { dynamicPriceInfo } = slotService;
  const customOption = dynamicPriceInfo?.customOptions?.[0];

  if (!customOption) {
    return null;
  }

  const serviceSlotId = getServiceSlotIdentifier(slotService.nestedSlot);
  const currentChoice =
    slotService.participantVariants?.[participantIndex]?.choices?.[0];

  // Seed the dropdown's initial selection from this participant's current choice.
  // Match on the choice's custom name (which is a PriceChoice's choiceId — the key
  // the dropdown selects by); fall back to the variant id. This covers both a
  // user-picked choice and one seeded from a calendar pre-selection.
  const isCurrentChoice = (option: PriceChoice) =>
    !!currentChoice &&
    (option.choiceId === currentChoice.custom ||
      (!!option.id && option.id === currentChoice.id));
  const options: PriceChoice[] = customOption.options.map((option) => ({
    ...option,
    numberOfParticipantsInitialValue: isCurrentChoice(option) ? 1 : 0,
  }));

  return (
    <PriceOptionDropdown
      // PriceOptionDropdown seeds its selection from `options` only at mount, and a
      // participant's choice can arrive after first render (seeded from the calendar
      // pre-selection via an effect) or change later — so include the current choice
      // in the key to remount and reflect it.
      key={`${serviceSlotId}-p${participantIndex}-${
        currentChoice?.custom ?? 'empty'
      }`}
      componentKey={`${serviceSlotId}-p${participantIndex}`}
      options={options}
      maxParticipants={1}
      showPrice={
        slotService.selectedPaymentOption.id ===
        ReservedPaymentOptionIds.SingleSession
      }
      label={t('app.booking-details.price-option.dropdown-label', {
        serviceName: slotService.service.name,
        optionName: customOption.name,
      })}
      disabled={status === FormStatus.PROCESSING_PAYMENT_DETAILS}
      onDropdownClose={(priceOptionData) =>
        onParticipantVariantSelect(
          priceOptionData,
          customOption.optionId,
          serviceSlotId,
          participantIndex,
        )
      }
    />
  );
};

export default ParticipantVariantPicker;
